<script lang="ts">
	import { format } from 'svelte-i18n';

	import { Alert, ButtonV2, toast } from '@Shelly/shelly-design-system';

	import { openExternalLink } from 'src/helpers/utilities';

	export let heading = '';
	export let message = '';
	export let recoveryLabel = '';
	export let onRecovery: (() => Promise<boolean>) | null = null;
	export let idPrefix = 'settings-item';

	const SUPPORT_URL = 'https://support.shelly.cloud/en/support/home';

	let recovering = false;

	const handleError = (error: unknown): void => {
		console.error(error);
	};

	const openSupport = (): void => {
		openExternalLink(SUPPORT_URL);
	};

	const handleRecovery = async (reset: () => void): Promise<void> => {
		if (!onRecovery || recovering) return;
		recovering = true;
		const ok = await onRecovery();
		recovering = false;
		if (!ok) {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}
		reset();
	};
</script>

<svelte:boundary onerror={handleError}>
	<slot />

	{#snippet failed(_error, reset)}
		<Alert
			type="danger"
			class="mb-m"
			heading={heading ||
				$format('settings_item_error_title', { default: 'This setting could not load' })}
			message={message ||
				$format('settings_item_error_message', {
					default:
						'Try again. If it still fails, contact Shelly support. Other settings are unaffected.',
				})}
		>
			<div slot="bottom" class="d-flex flex-wrap gap-s">
				<ButtonV2
					id={`${idPrefix}-retry`}
					size="s"
					variant="secondary"
					aria-label={$format('button_try_again', { default: 'Try again' })}
					on:click={reset}
				>
					{$format('button_try_again', { default: 'Try again' })}
				</ButtonV2>
				{#if onRecovery && recoveryLabel}
					<ButtonV2
						id={`${idPrefix}-recovery`}
						size="s"
						variant="secondary"
						loading={recovering}
						disabled={recovering}
						aria-label={recoveryLabel}
						on:click={() => handleRecovery(reset)}
					>
						{recoveryLabel}
					</ButtonV2>
				{/if}
				<ButtonV2
					id={`${idPrefix}-support`}
					size="s"
					variant="secondary"
					aria-label={$format('button_contact_shelly_support', {
						default: 'Contact Shelly support',
					})}
					on:click={openSupport}
				>
					{$format('button_contact_shelly_support', { default: 'Contact Shelly support' })}
				</ButtonV2>
			</div>
		</Alert>
	{/snippet}
</svelte:boundary>
